import 'uicomponents/uicomponents.css';
declare global {
  interface Window {
    featureFlag: EventAppFeatureFlags;
    serviceworkerprefix: any;
    addeventatc: any;
    mmsocket: WebSocket;
    gatewaysocket: WebSocket;
    idbKeyval: {
      get(key: string): any;
    };
    KJUR: any;
    mobileCheck: any;
    i18n: any;
    configs: any;
    metrics: any;
    Auth: any;
    router: any;
    logrocketSessionUrl: string;
    eventid: string;
    eventuserid: string;
    temporaryPdfRefsWhileRendering: any;
  }
}

import { Auth, Amplify } from 'aws-amplify';
import { uicomponents } from 'uicomponents';
import { createApp } from 'vue';
import { sync } from 'vuex-router-sync';

import analytics from './analytics';
import App from './App.vue';
import { mobileCheck } from './assets/utilfunctions';
import router from './router';
import { EventAppFeatureFlags, initializeFeatureFlags } from './services/featureFlag.service';
import store from './store';
import initializeFetchRegister from './store/fetchRegister';
import { deleteWrongCognitoDomainCookies } from './utils/cookies';
import { storeKey } from './utils/storeAccess';

import { getEnv } from '@/constants';
import { AuthMixin } from '@/mixins/AuthMixins';
import { getEventFromUberEndpoint } from '@/services/event.service';
import loggerService from '@/services/logger.service';
import { loadRuntimeConfig } from '@/services/runtimeConfig.service';
import { applySessionAuthSystem } from '@/utils/authSystemSwitch';
import { getEventIdFromUrl } from '@/utils/url';
import '@mdi/font/css/materialdesignicons.css';
import '@/styles/TextStyles.css';

const app = createApp(App);

function getSubdomain(hostname: string, level: number) {
  const augmentedHostname = '.' + hostname;
  let numDots = 0;
  let lastDotIndex = -1;
  for (let i = augmentedHostname.length - 1; i >= 0; --i) {
    if (augmentedHostname[i] === '.') {
      numDots++;
      lastDotIndex = i;
    }

    if (numDots === level + 1) {
      break;
    }
  }

  if (numDots !== level + 1) {
    loggerService.warn(
      "Couldn't get subdomain level: ",
      level,
      ' found only: ',
      numDots,
      ' dots but required: ',
      level + 1
    );
  }

  const cookieStorageDomain = augmentedHostname.slice(lastDotIndex + 1);
  loggerService.info('Setting cookie storage domain to: ', cookieStorageDomain);
  return cookieStorageDomain;
}

function getCurrentDomain() {
  const hostname = window.location.hostname;
  const cookieDomain = getEnv('VUE_APP_AUTH_COOKIE_DOMAIN');
  if (hostname.endsWith(getEnv('VUE_APP_COOKIE_BASE_DOMAIN'))) {
    return cookieDomain;
  }
  return getSubdomain(hostname, 2);
}

function addDataToWindow() {
  window.logrocketSessionUrl = 'notset';
  window.eventuserid = 'notset';
  window.eventid = 'notset';
  window.mobileCheck = mobileCheck;
  window.router = router;
}

export default function focused(el: any, binding: any, { value }: any): void {
  // Focus the element
  if (binding.value !== undefined && binding.value !== false) {
    el.focus();
  }
}

function initLoggerService() {
  const release = (window as any).configs?.BUILD_NUMBER || 'dev';
  const isRecorder = new URLSearchParams(window.location.search).get('record_mode') === 'true';

  loggerService
    .init({
      release,
      isDev: import.meta.env.MODE === 'development',
      logrocket: {
        enabled: getEnv('VUE_APP_LOGROCKET_ENABLED') === 'yes',
        appID: getEnv('VUE_APP_LOGROCKET_APP_ID'),
        options: {
          release,
          console: {
            isEnabled: true,
            shouldAggregateConsoleErrors: true
          },
          mergeIframes: true,
          childDomains: ['*'],
          ingestServer: getEnv('VUE_APP_LOGROCKET_INGEST_SERVER'),
          sdkServer: getEnv('VUE_APP_LOGROCKET_INGEST_SERVER'),
          // Headless recorder has no user input; default 30m idle timeout cuts sessions short.
          ...(isRecorder && { inactivityTimeThreshold: 12 * 60 * 60 * 1000 })
        }
      }
    })
    .catch(err => {
      console.error('Error initializing logger', err);
    });
}

function initAppCore() {
  app.config.warnHandler = () => null;
  app.config.errorHandler = err => {
    loggerService.error(err);
  };
  app.directive('focused', focused);
}

function initInitialAuthState(store: any) {
  return store.dispatch('forceNoCacheFetchUser');
}

function showLoadingScreen(): HTMLElement {
  const loader = document.createElement('div');
  loader.id = 'gc-init-loader';
  loader.style.cssText = [
    'position:fixed',
    'inset:0',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'background:#fff',
    'z-index:9999'
  ].join(';');
  loader.innerHTML = `
    <style>
      @keyframes gc-spin { to { transform: rotate(360deg); } }
      #gc-init-loader .gc-loader-wrap {
        display: flex; flex-direction: column; align-items: center; gap: 16px;
      }
      #gc-init-loader .gc-spinner {
        width: 40px; height: 40px;
        border: 3px solid #e5e7eb;
        border-top-color: #6366f1;
        border-radius: 50%;
        animation: gc-spin 0.75s linear infinite;
      }
      #gc-init-loader .gc-loader-text {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        font-size: 15px;
        color: #6b7280;
        letter-spacing: 0.01em;
      }
    </style>
    <div class="gc-loader-wrap">
      <div class="gc-spinner"></div>
      <p class="gc-loader-text">Getting your event ready&hellip;</p>
    </div>
  `;
  document.body.appendChild(loader);
  return loader;
}

function hideLoadingScreen(loader: HTMLElement): void {
  loader.style.opacity = '0';
  loader.style.transition = 'opacity 0.2s ease';
  setTimeout(() => loader.remove(), 200);
}

async function initRuntimeConfig(): Promise<void> {
  const eventId = getEventIdFromUrl();
  if (!eventId) {
    return;
  }

  const loader = showLoadingScreen();
  try {
    await getEventFromUberEndpoint(eventId, false, { first: true }, true);
    await loadRuntimeConfig();
    applySessionAuthSystem();
  } catch (error) {
    loggerService.warn('[main.ts] Error fetching event for runtime config:', error);
  } finally {
    hideLoadingScreen(loader);
  }
}

async function init() {
  initLoggerService();

  app.use(uicomponents, {
    $trackEvent: analytics.track
  });

  const i18n = (app as any).$i18n;
  window.i18n = i18n;

  const domain = getEnv('VUE_APP_AUTH_COOKIE_DOMAIN');
  if (domain !== 'localhost') {
    window.configs.VUE_APP_AUTH_COOKIE_DOMAIN = getCurrentDomain();
  }

  Auth.configure({
    storage: localStorage, // Store tokens in localStorage storage
    cookieStorage: null, // Disable cookie storage
    domain: getEnv('VUE_APP_AUTH_COOKIE_DOMAIN')
  });

  const awsconfig = window.configs?.VUE_APP_AMPLIFY_CONFIG_OVERRIDES;
  if (!awsconfig) {
    throw new Error(
      '[CONFIG] Missing VUE_APP_AMPLIFY_CONFIG_OVERRIDES. Authentication will not function without this configuration.'
    );
  } else {
    const amplifyConfig = {
      aws_cognito_identity_pool_id: awsconfig.AWS_COGNITO_IDENTITY_POOL_ID,
      aws_cognito_region: awsconfig.AWS_COGNITO_REGION,
      aws_project_region: awsconfig.AWS_PROJECT_REGION,
      aws_user_files_s3_bucket: awsconfig.AWS_USER_FILES_S3_BUCKET,
      aws_user_files_s3_bucket_region: awsconfig.AWS_USER_FILES_S3_BUCKET_REGION,
      aws_user_pools_id: awsconfig.AWS_USER_POOLS_ID,
      aws_user_pools_web_client_id: awsconfig.AWS_USER_POOLS_WEB_CLIENT_ID,
      oauth: {}
    };

    deleteWrongCognitoDomainCookies(amplifyConfig.aws_user_pools_web_client_id);

    Amplify.configure(amplifyConfig);
  }

  sync(store, router);

  // Register service worker
  if ('serviceWorker' in navigator && getEnv('VUE_APP_SERVICE_WORKER_ENABLED') === 'yes') {
    const path = window.serviceworkerprefix ? `${window.serviceworkerprefix}/sw.js` : '/sw.js';
    navigator.serviceWorker.register(path).then(
      () => {
        // no code
      },
      err => {
        loggerService.warn('Service worker registration failed!', err);
      }
    );
  }

  if (navigator.appVersion.indexOf('Win') !== -1) {
    const body = document.querySelector('body');
    if (body) {
      body.classList.add('windowsOs');
    }
  }

  // Initialize the fetch interceptors - MUST be before any @goldcast/api calls
  initializeFetchRegister();

  await initRuntimeConfig();

  initAppCore();

  initInitialAuthState(store)
    .then(() => {
      app.mixin(AuthMixin);
      //added for those routes which are not dependent on event loaded e.g. ungated tech check
      store.dispatch('event/setLocale', { default_language: 'en', id: 'no-event-id' });
      app.config.globalProperties.$t = i18n.global.t;
      return app.use(i18n).use(router).use(store, storeKey).mount('#app');
    })
    .then(() => {
      sessionStorage.removeItem('chunk-reload-attempted');
      // Skip non-streaming init on event pages — enterEvent will initialize with streaming
      const isEventPage = window.location.pathname.startsWith('/e/');
      if (!isEventPage) {
        return initializeFeatureFlags(false).catch(err => {
          loggerService.error('Error initializing feature flags:', err);
        });
      }
    })
    .catch(err => {
      loggerService.error('Critical error during app initialization:', err);
    });

  const sdkBaseUrl = getEnv('VUE_APP_GOLDCAST_SDK_URL');
  if (sdkBaseUrl) {
    const script = document.createElement('script');
    script.src = `${sdkBaseUrl}/v1.0.208_161/embed.js`;
    script.onerror = () => {
      console.error(`[CONFIG] Failed to load embedded SDK from: ${sdkBaseUrl}`);
    };
    document.head.appendChild(script);
  }

  addDataToWindow();
}

window.addEventListener('vite:preloadError', () => {
  if (!sessionStorage.getItem('chunk-reload-attempted')) {
    sessionStorage.setItem('chunk-reload-attempted', '1');
    window.location.reload();
  }
});

init();
